/**
 * <anja-size-chart>: interactive size chart.
 *  - cm/in toggle converts numeric cells (1 in = 2.54 cm), labels update.
 *  - Age finder suggests sizes from the chart's own age ranges ("4-6 Years"); on a boundary it
 *    recommends sizing up, as the chart's fit guidance says.
 *  - Highlights the size currently selected on the product page and can select a size for the shopper.
 */
const CM_PER_IN = 2.54;

/** @param {string} label */
function ageRange(label) {
  const m = label.match(/(\d+)\s*[-–]\s*(\d+)/);
  return m ? [Number(m[1]), Number(m[2])] : null;
}

/** @param {string} value */
const norm = (value) => value.toLowerCase().replace(/\s+/g, ' ').trim();

class AnjaSizeChart extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.baseUnit = this.dataset.unit || 'cm';
    this.rows = /** @type {HTMLTableRowElement[]} */ (Array.from(this.querySelectorAll('tr[data-sc-size]')));
    this.result = /** @type {HTMLElement | null} */ (this.querySelector('[data-sc-result]'));
    const t = (key, fallback) => this.closest('[data-sc-strings]')?.getAttribute(`data-${key}`) || fallback;
    this.strings = {
      recommend: t('recommend', 'Recommended: [size]'),
      between: t('between', '[a] or [b]. Between sizes? Size up for growth and comfort.'),
      none: t('none', 'No size in this chart for that age. Ask the ANJA team for help.'),
    };

    this.addEventListener(
      'click',
      (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        const unit = target.closest('[data-sc-unit]');
        if (unit instanceof HTMLElement) this.#setUnit(unit.dataset.scUnit || 'cm');
        const tab = target.closest('[data-sc-tab]');
        if (tab instanceof HTMLElement) this.#selectTab(tab);
        const select = target.closest('[data-sc-select]');
        if (select instanceof HTMLElement) this.#chooseSize(select.dataset.scSelect || '');
        const chip = target.closest('[data-sc-chip]');
        if (chip instanceof HTMLElement) {
          this.dataset.cardTouched = 'true';
          this.#showCard(chip.dataset.scChip || '');
        }
        const full = target.closest('[data-sc-full]');
        if (full instanceof HTMLElement) {
          const open = this.classList.toggle('is-full');
          full.setAttribute('aria-expanded', String(open));
        }
      },
      { signal }
    );
    this.querySelector('[data-sc-age]')?.addEventListener('change', (e) => this.#findByAge(/** @type {HTMLSelectElement} */ (e.target).value), { signal });
    this.querySelector('[role="tablist"]')?.addEventListener(
      'keydown',
      (event) => {
        const key = /** @type {KeyboardEvent} */ (event).key;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;
        const tabs = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-sc-tab]')));
        const i = tabs.indexOf(/** @type {HTMLElement} */ (document.activeElement));
        const next = key === 'Home' ? 0 : key === 'End' ? tabs.length - 1 : (i + (key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        tabs[next]?.focus();
        this.#selectTab(tabs[next]);
      },
      { signal }
    );

    this.highlightSelected();
    document.addEventListener('change', () => this.highlightSelected(), { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {HTMLElement} tab */
  #selectTab(tab) {
    this.querySelectorAll('[data-sc-tab]').forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.setAttribute('tabindex', on ? '0' : '-1');
      const panel = this.querySelector(`#${CSS.escape(t.getAttribute('aria-controls') || '')}`);
      if (panel instanceof HTMLElement) panel.hidden = !on;
    });
  }

  /** @param {string} unit */
  #setUnit(unit) {
    this.querySelectorAll('[data-sc-unit]').forEach((b) => b.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (b).dataset.scUnit === unit)));
    this.querySelectorAll('[data-sc-unit-label]').forEach((l) => (l.textContent = l.closest('dt') ? `(${unit})` : ` (${unit})`));
    this.querySelectorAll('[data-sc-value]').forEach((cell) => {
      const raw = /** @type {HTMLElement} */ (cell).dataset.scValue || '';
      cell.textContent = raw.replace(/\d+(?:\.\d+)?/g, (n) => {
        const value = Number(n);
        if (unit === this.baseUnit) return n;
        const converted = unit === 'in' ? value / CM_PER_IN : value * CM_PER_IN;
        return String(Math.round(converted * 10) / 10);
      });
    });
  }

  /** @param {string} value */
  #findByAge(value) {
    this.rows.forEach((r) => r.classList.remove('is-recommended'));
    if (!this.result) return;
    if (!value) {
      this.result.hidden = true;
      return;
    }
    const age = Number(value);
    const matches = this.rows.filter((row) => {
      const range = ageRange(row.dataset.scSize || '');
      return range && age >= range[0] && age <= range[1];
    });
    this.result.hidden = false;
    if (!matches.length) {
      this.result.textContent = this.strings.none;
      return;
    }
    const pick = matches[matches.length - 1];
    pick.classList.add('is-recommended');
    this.#showCard(pick.dataset.scSize || '', true);
    pick.scrollIntoView({ block: 'nearest' });
    this.result.textContent =
      matches.length > 1
        ? this.strings.between.replace('[a]', matches[0].dataset.scSize || '').replace('[b]', pick.dataset.scSize || '')
        : this.strings.recommend.replace('[size]', pick.dataset.scSize || '');
  }

  /** Values currently chosen in the product page's variant picker (radios or selects). */
  #pickerValues() {
    const scope = document.querySelector('variant-picker') || document;
    const values = new Set();
    scope.querySelectorAll('input[type="radio"]:checked, select').forEach((el) => {
      const v = /** @type {HTMLInputElement} */ (el).value;
      if (v) values.add(norm(v));
    });
    return values;
  }

  /** @param {string} size @param {boolean} [recommended] */
  #showCard(size, recommended = false) {
    this.querySelectorAll('[data-sc-chip]').forEach((c) => {
      const on = norm(/** @type {HTMLElement} */ (c).dataset.scChip || '') === norm(size);
      c.setAttribute('aria-pressed', String(on));
      c.classList.toggle('is-recommended', on && recommended);
      if (on && this.closest('dialog')?.open) c.scrollIntoView({ block: 'nearest', inline: 'center' });
    });
    this.querySelectorAll('[data-sc-card]').forEach((card) => {
      /** @type {HTMLElement} */ (card).hidden = norm(/** @type {HTMLElement} */ (card).dataset.scCard || '') !== norm(size);
    });
  }

  highlightSelected() {
    const values = this.#pickerValues();
    let current = '';
    this.querySelectorAll('[data-sc-card]').forEach((card) => {
      const on = values.has(norm(/** @type {HTMLElement} */ (card).dataset.scCard || ''));
      const badge = /** @type {HTMLElement | null} */ (card.querySelector('[data-sc-badge]'));
      if (badge) badge.hidden = !on;
      if (on) current = /** @type {HTMLElement} */ (card).dataset.scCard || '';
    });
    this.querySelectorAll('[data-sc-chip]').forEach((c) => c.classList.toggle('is-current', values.has(norm(/** @type {HTMLElement} */ (c).dataset.scChip || ''))));
    if (current && !this.dataset.cardTouched) this.#showCard(current);
    this.rows.forEach((row) => {
      const on = values.has(norm(row.dataset.scSize || ''));
      row.classList.toggle('is-selected', on);
      const badge = /** @type {HTMLElement | null} */ (row.querySelector('[data-sc-badge]'));
      if (badge) badge.hidden = !on;
    });
  }

  /** @param {string} size */
  #chooseSize(size) {
    const scope = document.querySelector('variant-picker');
    if (!scope) return;
    const input = /** @type {HTMLInputElement | undefined} */ (
      Array.from(scope.querySelectorAll('input[type="radio"]')).find((i) => norm(/** @type {HTMLInputElement} */ (i).value) === norm(size))
    );
    if (input) {
      input.click();
    } else {
      const select = Array.from(scope.querySelectorAll('select')).find((s) =>
        Array.from(/** @type {HTMLSelectElement} */ (s).options).some((o) => norm(o.value) === norm(size))
      );
      if (select instanceof HTMLSelectElement) {
        select.value = Array.from(select.options).find((o) => norm(o.value) === norm(size))?.value || select.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    setTimeout(() => this.highlightSelected(), 50);
    this.closest('dialog')?.close();
  }
}

if (!customElements.get('anja-size-chart')) customElements.define('anja-size-chart', AnjaSizeChart);
